import { useState } from 'react';
import { NavLink, useResolvedPath, useMatch } from 'react-router';
import { OidcProtectedComponent, useOidc } from '@nohara-dxdev/react-oidc';
import { Building2, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from '@/components/ui/sidebar';

import { version as appVersion } from '../../../package.json';

import { PageHeader } from './page-header';

import type { PermissionOrGroupOrString, Scope } from '@nohara-dxdev/oidc-core';

/**
 * Shape of a sidebar entry. `requireAll` and `scope` mirror the props that
 * `OidcProtectedComponent` accepts, so callers can pass the typed permission
 * constants from `@nohara-dxdev/oidc-core` without re-casting. `external`
 * entries open `path` as a full URL in a new tab (e.g. the home portal);
 * internal entries route through react-router and reflect the active route.
 */
type NavChild = {
  path: string;
  label: string;
};

type NavItem = {
  path: string;
  icon: React.ReactNode;
  label: string;
  external?: boolean;
  requireAll?: PermissionOrGroupOrString[];
  scope?: Scope;
  /** Sub-entries rendered as a collapsible list under this item (e.g. 物件一覧). */
  children?: NavChild[];
};

type SidebarNavProps = {
  items: NavItem[];
  /**
   * Name of the org currently being acted in; shown in the footer so the
   * operator can tell which org's data they are looking at.
   */
  orgName?: string;
  paths: {
    logout: string;
  };
  variant?: 'external' | 'internal';
};

// Internal route: highlight when the current location is at or below the item's
// path (so プロジェクト stays active on project detail pages, not just the list).
const InternalNavButton = ({ item }: { item: NavItem }) => {
  const resolved = useResolvedPath(item.path);
  const match = useMatch({ path: resolved.pathname, end: false });
  return (
    <SidebarMenuButton asChild isActive={Boolean(match)} className='rounded-lg'>
      <NavLink to={item.path}>
        {item.icon}
        <span>{item.label}</span>
      </NavLink>
    </SidebarMenuButton>
  );
};

const ExternalNavButton = ({ item }: { item: NavItem }) => (
  <SidebarMenuButton asChild className='rounded-lg'>
    <a href={item.path} target='_blank' rel='noopener noreferrer'>
      {item.icon}
      <span className='flex-1'>{item.label}</span>
      {/* Marks the entry as leaving this app for the home portal in a new tab. */}
      <ExternalLink className='h-4 w-4 shrink-0' aria-hidden='true' />
    </a>
  </SidebarMenuButton>
);

const NavButton = ({ item }: { item: NavItem }) =>
  item.external ? <ExternalNavButton item={item} /> : <InternalNavButton item={item} />;

// A single project (or other child) entry: exact-match active so it highlights
// only on its own detail page, not on the parent list.
const SubNavButton = ({ child }: { child: NavChild }) => {
  const resolved = useResolvedPath(child.path);
  const match = useMatch({ path: resolved.pathname, end: false });
  return (
    <SidebarMenuSubButton asChild isActive={Boolean(match)}>
      <NavLink to={child.path}>
        <Building2 className='h-4 w-4' />
        <span>{child.label}</span>
      </NavLink>
    </SidebarMenuSubButton>
  );
};

// Item with a collapsible child list (物件一覧). The parent row keeps its own
// link; a chevron toggles the sub-list, expanded by default.
const CollapsibleNavItem = ({ item }: { item: NavItem }) => {
  const [open, setOpen] = useState(true);
  const children = item.children ?? [];

  return (
    <>
      <SidebarMenuItem>
        <div className='flex items-center'>
          <div className='min-w-0 flex-1'>
            <NavButton item={item} />
          </div>
          {children.length > 0 && (
            <button
              type='button'
              onClick={() => setOpen((prev) => !prev)}
              aria-label={item.label}
              aria-expanded={open}
              className='flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:outline-none'
            >
              {open ? (
                <ChevronDown className='size-4' aria-hidden='true' />
              ) : (
                <ChevronRight className='size-4' aria-hidden='true' />
              )}
            </button>
          )}
        </div>
      </SidebarMenuItem>
      {open && children.length > 0 && (
        <SidebarMenuSub>
          {children.map((child) => (
            <SidebarMenuSubItem key={child.path}>
              <SubNavButton child={child} />
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </>
  );
};

const NavItemBody = ({ item }: { item: NavItem }) =>
  item.children ? (
    <CollapsibleNavItem item={item} />
  ) : (
    <SidebarMenuItem>
      <NavButton item={item} />
    </SidebarMenuItem>
  );

const PermissionGatedNavItem = ({ item }: { item: NavItem }) => {
  if (item.requireAll && item.requireAll.length > 0) {
    return (
      <OidcProtectedComponent
        requireAll={item.requireAll}
        scope={item.scope}
        fallbackElement={null}
      >
        <NavItemBody item={item} />
      </OidcProtectedComponent>
    );
  }

  return <NavItemBody item={item} />;
};

export const SidebarNav = ({
  items,
  orgName,
  paths: navPaths,
  children,
  variant = 'internal',
}: SidebarNavProps & { children?: React.ReactNode }) => {
  const { user } = useOidc();
  const { t } = useTranslation();

  return (
    <div className='flex h-svh flex-col'>
      <PageHeader variant={variant} userName={user?.name} logoutHref={navPaths.logout} />
      <SidebarProvider className='min-h-0 flex-1'>
        <Sidebar collapsible='none' className='h-full border-r border-sidebar-border'>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => (
                    <PermissionGatedNavItem key={item.label} item={item} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          {/* The divider sits above the version line only, so the org label reads as
              part of the nav rather than as chrome. */}
          <SidebarFooter>
            {orgName && (
              <span className='text-sm font-medium text-sidebar-foreground'>
                {t('navigation.orgName', { name: orgName })}
              </span>
            )}
            <span className='border-t border-sidebar-border pt-2 text-xs text-muted-foreground'>
              {t('navigation.appVersion', { version: `v${appVersion}` })}
            </span>
          </SidebarFooter>
        </Sidebar>

        <SidebarInset className='min-w-0 overflow-y-auto p-6'>{children}</SidebarInset>
      </SidebarProvider>
    </div>
  );
};
